import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Capacitor } from '@capacitor/core';
import { environment } from 'environments/environment';
import { CodaltComponent } from '../../codalt.component';
import { Customer } from '../../customer';
import { CustomerProjectPhaseActivity } from '../../customer-project-phase-activity.interface';
import { CustomerProjectPhaseReply } from '../../customer-project-phase-reply.interface';
import { CustomerProjectPhase } from '../../customer-project-phase.interface';
import { FileItem } from '../../document-upload/file-item';
import { StorageName } from '../../document-upload/storage-name.enum';
import { ImageViewerDialogComponent } from '../../documents/image-viewer-dialog/image-viewer-dialog.component';
import { PdfViewerDialogComponent } from '../../documents/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { LocalStorage } from '../../local-storage.class';
import { ConfirmDialogService } from '../../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectPhaseReplyService } from '../../services/customer-project/customer-project-phase-reply.service';
import { FileService } from '../../services/file.service';
import { NativeFileViewerService } from '../../services/native-file-viewer/native-file-viewer.service';
import { User } from '../../user.interface';

@Component({
    selector: 'app-customer-project-phase-replies',
    templateUrl: './customer-project-phase-replies.component.html',
    styleUrls: ['./customer-project-phase-replies.component.scss'],
    standalone: false,
})
export class CustomerProjectPhaseRepliesComponent extends CodaltComponent implements OnInit, OnDestroy {
    @Input()
    highlightId: number;

    @Input()
    selectedActivity: CustomerProjectPhaseActivity;

    @Input()
    phase: CustomerProjectPhase;
    @Input()
    replies: CustomerProjectPhaseReply[];
    inputActive = false;
    fcReplyContent = new FormControl();
    customer: Customer;
    user: User;
    disableDragDrop = Capacitor.isNativePlatform();
    stagedFiles: FileItem[] = [];

    @ViewChild('replyInput', { static: true })
    private replyInput;

    constructor(
        private replyService: CustomerProjectPhaseReplyService,
        private fileService: FileService,
        private dialog: MatDialog,
        private nativeFileViewer: NativeFileViewerService,
        private confirmDialog: ConfirmDialogService
    ) {
        super();
    }

    ngOnInit(): void {
        this.customer = LocalStorage.getCustomer();
        this.user = LocalStorage.getUser();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        for (const file of this.stagedFiles) {
            this.fileService.delete(`${this.phase.id}/${file.basename}`, StorageName.projectreplies).subscribe();
        }
    }

    upload(file: FileItem) {
        this.stagedFiles.push(file);
    }

    removeStagedFile(file: FileItem) {
        this.stagedFiles.splice(this.stagedFiles.indexOf(file), 1);
        this.fileService.delete(`${this.phase.id}/${file.basename}`, StorageName.projectreplies).subscribe();
    }

    enterKey(event: KeyboardEvent) {
        if (event.key === 'Enter' && event.ctrlKey && (this.fcReplyContent.valid || this.stagedFiles.length)) {
            this.save();
        }
    }

    moveToActivity(reply: CustomerProjectPhaseReply) {
        this.replyService.save(reply).subscribe(data => {
            for (const updated of data.data) {
                const r = this.replies.find(r => r.id === updated.id);
                if (r) {
                    r.customer_projectphase_activity_id = updated.customer_projectphase_activity_id;
                }
            }
        });
    }

    save(event?: any) {
        if (event) {
            event.stopPropagation();
        }
        if (this.fcReplyContent.disabled) {
            return; // To prevent concurrency issues
        }
        if (!this.fcReplyContent.value && !this.stagedFiles.length) {
            return;
        }
        this.fcReplyContent.disable();
        const submittedFiles = this.stagedFiles;
        this.stagedFiles = [];
        const reply = {
            name: 'Reactie',
            text: this.fcReplyContent.value ?? '',
            customer_projectphase_id: this.phase.id,
            customer_projectphase_activity_id: this.selectedActivity?.id,
            documents: submittedFiles.map(file => ({ name: file.filename, text: file.basename })),
        } as CustomerProjectPhaseReply;
        this.replyService.save(reply).subscribe(
            data => {
                this.replies.push(...data.data);
                this.fcReplyContent.reset();
                this.fcReplyContent.enable();
            },
            () => {
                this.stagedFiles.push(...submittedFiles);
                this.fcReplyContent.enable();
            }
        );
    }

    delete(reply: CustomerProjectPhaseReply) {
        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet je zeker dat je jouw reactie van
            ${formatDate(reply.updated_at, 'd MMMM yyyy, H:mm', 'nl')} uur
            wilt verwijderen?`,
                'Verwijderen',
                'Behouden'
            )
            .then(() =>
                this.replyService.delete(reply.id).subscribe(data => {
                    for (const deleted of data.data) {
                        const r = this.replies.find(r => r.id === deleted.id);
                        if (!r) {
                            continue;
                        }
                        if (r.is_document) {
                            this.fileService
                                .delete(`${this.phase.id}/${r.text}`, StorageName.projectreplies)
                                .subscribe();
                        }
                        this.replies.splice(this.replies.indexOf(r), 1);
                    }
                })
            )
            .catch(() => {});
    }

    openReply(reply: CustomerProjectPhaseReply) {
        if (reply.is_document) {
            if (['.jpg', 'jpeg', '.png'].indexOf(reply.text.substr(-4)) !== -1) {
                this.dialog.open(ImageViewerDialogComponent, {
                    panelClass: 'image-viewer-dialog',
                    data: {
                        documentImages: this.replies
                            .filter(f => ['.jpg', 'jpeg', '.png'].indexOf(f.text.substr(-4)) !== -1)
                            .map(
                                d =>
                                    `${environment.apiEndpoint}file/thumb?path=${d.customer_projectphase_id}/${d.text}&current_customer_id=${this.customer.id}&secure=${StorageName.projectreplies}&access_token=${LocalStorage.getUserToken()}`
                            ),
                        viewIndex: this.replies
                            .filter(f => ['.jpg', 'jpeg', '.png'].indexOf(f.text.substr(-4)) !== -1)
                            .indexOf(reply),
                    },
                });
            } else if (reply.text.substr(-3) === 'pdf') {
                const url = `${environment.apiEndpoint}file/thumb?path=${reply.customer_projectphase_id}/${reply.text}&current_customer_id=${this.customer.id}&secure=${StorageName.projectreplies}&access_token=${LocalStorage.getUserToken()}`;
                if (Capacitor.isNativePlatform()) {
                    this.nativeFileViewer
                        .openNativePdf(url, `${reply.id}-${reply.text}`)
                        .catch(error => console.error('open failed', error));
                } else {
                    this.dialog.open(PdfViewerDialogComponent, {
                        width: '99%',
                        maxWidth: '99%',
                        maxHeight: '99%',
                        height: '99%',
                        data: {
                            pdf: url,
                            name: reply.name,
                        },
                    });
                }
            } else {
                window.open(
                    `${environment.apiEndpoint}file/thumb?path=${reply.customer_projectphase_id}/${reply.text}&current_customer_id=${this.customer.id}&secure=${StorageName.projectreplies}&access_token=${LocalStorage.getUserToken()}`
                );
            }
        }
    }
}
